/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-10 10:33:30
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { DisplayText, Badge, FormLayout, Icon, Link } from '@shopify/polaris';
import { ReadTimeMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import TrendingProducts from './trending-products';
import HandPickedProducts from './hand-picked-products';
import FrequentlyBoughtTogether from './frequently-bought-together';
import PersonalizedRecommendation from './personalized-recommendation';
import RelatedItems from './related-items';
import GrantPermissionsNotif from 'src/components/views/recommendation/grant-permissions-notif';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';

import { getShop, buildUpdateAppPermissionURL } from 'src/helpers';
import { URL } from 'src/constants/links';
import {
  PERSONALIZED_RECOMMENDATION,
  RECOMMENDATION_WIDGET_PAGE_TYPES,
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES
} from 'src/constants/recommendation';

import type { RecommendationWidgetType } from 'src/models/recommendation';
type Props = WithI18nProps & {
  formValues: RecommendationWidgetType,
  recommendationWidget: RecommendationWidgetType,
  host: string,
  updateField: Function,
  aimlPermission: boolean,
  syncOrderStatus: {}
};

class General extends Component<Props, State> {
  render() {
    const {
      i18n,
      formValues,
      recommendationWidget,
      host,
      updateField,
      syncOrderStatus,
      aimlPermission
    } = this.props;

    let showGrantPermissionNoti = false;
    const { pageType, recommendationType, AIBasedRecommendProduct, secondaryAlgorithm } =
      recommendationWidget;
    const pageTypeName = _.get(RECOMMENDATION_WIDGET_PAGE_TYPES, `${pageType}.name`, '');
    const recommendationTypeName = _.get(
      RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES,
      `${recommendationType}.name`,
      ''
    );

    let extraFields = '';
    switch (recommendationType) {
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['trending-products'].value: {
        extraFields = <TrendingProducts aimlPermission={aimlPermission} />;
        break;
      }
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['hand-picked-products'].value: {
        extraFields = <HandPickedProducts pageType={pageType} />;
        break;
      }
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value: {
        showGrantPermissionNoti = !aimlPermission;
        extraFields = (
          <FrequentlyBoughtTogether
            updateField={updateField}
            formValues={formValues}
            host={host}
            AIBasedRecommendProduct={AIBasedRecommendProduct}
            secondaryAlgorithm={secondaryAlgorithm}
            aimlPermission={aimlPermission}
            syncOrderStatus={syncOrderStatus}
          />
        );
        break;
      }
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value: {
        showGrantPermissionNoti = !aimlPermission;
        extraFields = (
          <RelatedItems
            updateField={updateField}
            formValues={formValues}
            host={host}
            AIBasedRecommendProduct={AIBasedRecommendProduct}
            secondaryAlgorithm={secondaryAlgorithm}
            aimlPermission={aimlPermission}
            syncOrderStatus={syncOrderStatus}
          />
        );
        break;
      }
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['recently-purchased'].value: {
        showGrantPermissionNoti = !aimlPermission;
        break;
      }
      case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[PERSONALIZED_RECOMMENDATION].value: {
        showGrantPermissionNoti = !aimlPermission;
        extraFields = (
          <PersonalizedRecommendation
            updateField={updateField}
            formValues={formValues}
            host={host}
            AIBasedRecommendProduct={AIBasedRecommendProduct}
            secondaryAlgorithm={secondaryAlgorithm}
            aimlPermission={aimlPermission}
            syncOrderStatus={syncOrderStatus}
          />
        );
        break;
      }
      default:
    }

    return (
      <div>
        <div className="setup-content-title">
          <DisplayText size="large">
            {i18n.translate('AdminHTML.Recommendation.NewWidgetItem.stepChooseSettingsTitle')}
          </DisplayText>
          {recommendationType ===
            RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['hand-picked-products'].value && (
            <div className="help-doc">
              <RenderTooltip
                content={i18n.translate(
                  'AdminHTML.Recommendation.NewWidgetItem.stepChooseSettingsHelpDocTooltip'
                )}
                children={
                  <span className="help-doc-content">
                    <Icon source={ReadTimeMinor} color="interactive" />
                    <Link external url={URL['RecommendationHandPickedSettingsLearnMore']}>
                      {i18n.translate(
                        'AdminHTML.Recommendation.NewWidgetItem.stepChooseSettingsHelpDoc'
                      )}
                    </Link>
                  </span>
                }
              />
            </div>
          )}
          <div className="sub-title">
            <Badge>{pageTypeName ? i18n.translate(pageTypeName) : pageType}</Badge>
            <Badge>
              {recommendationTypeName ? i18n.translate(recommendationTypeName) : recommendationType}
            </Badge>
          </div>
        </div>
        <FormLayout>
          {showGrantPermissionNoti && (
            <GrantPermissionsNotif
              permissionURL={buildUpdateAppPermissionURL(getShop())}
              recommendationType={recommendationType}
            />
          )}
          <div className="extra-fields">{extraFields}</div>
        </FormLayout>
      </div>
    );
  }
}

export default withI18n()(General);
